import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Isvg from 'react-inlinesvg';
import _isNil from 'lodash/isNil';

import InfoBanner from 'components/InfoBanner/InfoBanner';
import Message from 'components/Message';
import { TooltipToggle, TooltipContent } from 'components/Tooltip';
import When from 'components/When';

import { ICONS } from 'constants/icons';

import { bem } from 'helpers/class-name';
import { httpClient } from 'helpers/http';
import { makePath } from 'helpers/link-generation';

import { selectUserId, selectActiveVenue } from 'selectors/general';
import { selectLocaleData } from 'selectors/locale-data';

import URLS from 'constants/urls';

import { messages as servicesMessages } from '../../Services/Services.messages';
import PricingStatusStepper, { getPricingStatusView } from '../PricingStatusStepper';

import PricingInputs from './PricingInputs';
import { buildRetailRateRequestPayload } from './servicePricingSection.helpers';
import { messages } from './ServicePricingSection.messages';

import './ServicePricingSection.scss';

export const PRICING_MODE = {
  INPUT: 'input',
  READONLY: 'readonly',
};

const { block, element } = bem('service-pricing-section');

const ServicePricingSection = forwardRef((props, ref) => {
  const {
    mode,
    currentPrice,
    priceLabel,
    priceTooltip,
    prefillEnabled,
    onContactSupport,
    isAlreadyBookable,
    retailRateRequest,
  } = props;

  const hasStatus = !!getPricingStatusView(retailRateRequest);

  const venue = useSelector(selectActiveVenue);
  const userId = useSelector(selectUserId);
  const { locale } = useSelector(selectLocaleData);

  const proofFileUploadRef = useRef(null);

  const servicePriceDescriptionMessage =
    venue.countryCode === 'USA' ? messages.servicePriceDescriptionUSA : messages.servicePriceDescription;

  // The parent form orchestrates collect proofs -> create entity -> submit
  // pricing, so a failed upload aborts before the entity is created.
  useImperativeHandle(
    ref,
    () => ({
      collectProofs: async () => {
        if (!proofFileUploadRef.current) return [];
        return proofFileUploadRef.current.collectProofs();
      },

      // Proof is satisfied by a link OR ≥1 file (both partner types).
      validateProof: ({ formValues, fileProofs = [] }) => {
        const hasLink = !!formValues.linkToPrice && formValues.linkToPrice.trim() !== '';
        if (hasLink || fileProofs.length > 0) return { valid: true };
        return { valid: false, error: messages.proofRequired };
      },

      // Caller supplies whichever identifier it has: msi_service_uuid (manual
      // services) or class_id (LAPI classes).
      submitPricing: async ({ classId, msiServiceUuid, formValues, fileProofs = [] }) => {
        if (_isNil(classId) && _isNil(msiServiceUuid)) {
          throw new Error('submitPricing requires either classId or msiServiceUuid');
        }

        const request = buildRetailRateRequestPayload({
          classId,
          msiServiceUuid,
          venue,
          formValues,
          fileProofs,
          userId,
        });
        return httpClient.post(URLS.manageServices.services.submitRetailRateRequest(), request, {
          ignoreForbiddenError: true,
        });
      },
    }),
    [venue, userId]
  );

  if (mode === PRICING_MODE.READONLY) {
    return (
      <div className={block()}>
        <hr className="mv+" />

        <Message as="p" message={messages.pricing} className="text--medium text--semibold" />

        <div className={element('current-payout-subheading-container')}>
          <Message as="p" message={priceLabel || messages.payout} className={element('current-payout-subheading')} />

          <TooltipToggle className={element('current-payout-info-toggle')} name="current-payout-tooltip">
            <Isvg src={ICONS.infoGrey} className={element('current-payout-info-icon')} />
          </TooltipToggle>

          <TooltipContent hoverable name="current-payout-tooltip">
            <Message
              message={priceTooltip || servicesMessages.details.priceTooltip}
              values={{
                link: text => (
                  <a href={makePath.partnerTaxDefinition(locale)} target="_blank" rel="noopener noreferrer">
                    {text}
                  </a>
                ),
              }}
            />
          </TooltipContent>
        </div>

        <When predicate={!_isNil(currentPrice)}>
          <Message
            as="p"
            format="currency"
            currency={venue.currencyCode}
            value={currentPrice}
            className={element('current-payout-value')}
          />
        </When>

        <When predicate={_isNil(currentPrice)}>
          <p className={element('current-payout-value')}>N/A</p>
        </When>

        {hasStatus ? (
          <>
            <PricingStatusStepper retailRateRequest={retailRateRequest} />
            {onContactSupport && (
              <button type="button" onClick={onContactSupport} className={element('contact-support-link')}>
                <Message message={messages.contactSupport} />
              </button>
            )}
          </>
        ) : (
          <InfoBanner
            icon={ICONS.infoBrand}
            subtitle={messages.pricingUpdateInfoBanner}
            callToAction={messages.contactSupport}
            onClick={onContactSupport}
          />
        )}
      </div>
    );
  }

  return (
    <div className={block()}>
      <hr className="mv+" />

      <Message as="p" message={messages.pricing} className="text--medium text--semibold" />

      {hasStatus && <PricingStatusStepper retailRateRequest={retailRateRequest} />}

      <PricingInputs
        venue={venue}
        userId={userId}
        locale={locale}
        prefillEnabled={prefillEnabled}
        proofFileUploadRef={proofFileUploadRef}
        servicePriceDescriptionMessage={servicePriceDescriptionMessage}
        showVerificationBanner={!isAlreadyBookable}
      />
    </div>
  );
});

ServicePricingSection.displayName = 'ServicePricingSection';

ServicePricingSection.propTypes = {
  mode: PropTypes.oneOf([PRICING_MODE.INPUT, PRICING_MODE.READONLY]),
  currentPrice: PropTypes.number,
  priceLabel: PropTypes.shape({ id: PropTypes.string }),
  priceTooltip: PropTypes.shape({ id: PropTypes.string }),
  prefillEnabled: PropTypes.bool,
  onContactSupport: PropTypes.func,
  // Suppresses the price-verification banner for services that are already
  // bookable — they predate this feature and never had a pricing request.
  isAlreadyBookable: PropTypes.bool,
  retailRateRequest: PropTypes.shape({
    status: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    status_reason: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    statusReason: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

ServicePricingSection.defaultProps = {
  mode: PRICING_MODE.INPUT,
  prefillEnabled: true,
};

export default ServicePricingSection;
